/**
 * Copyright (c) 2026 Zoldier & LCN Softwares All rights reserved.
 *
 * This software is the confidential and proprietary information of Zoldier & LCN Software.
 * You shall not disclose such confidential information and shall use it only
 * in accordance with the terms of the license agreement you entered into with
 * Zoldier & LCN Software.
 */

import { useLocale } from '../../hooks/useLocale';
import { DEFAULT_SEO } from '../../lib/seo';

/**
 * Renders default SEO meta tags for the current locale.
 *
 * Uses React 19 native support for hoisting `<title>` and `<meta>` tags from
 * any component into `<head>`. The document head updates automatically when
 * the locale changes (no `useEffect` or third-party head manager required).
 *
 * Outputs the full set of standard, Open Graph, and Twitter Card meta elements
 * sourced from `DEFAULT_SEO` keyed by the active `SupportedLocale`. Mount this
 * component once at the root level; per-route overrides can be added by
 * rendering additional `<title>` or `<meta>` elements closer to the leaf.
 *
 * @returns A React fragment containing `<title>`, `<meta>`, and `<link>` elements
 *   that React 19 hoists into `<head>` automatically.
 * @since 1.0.1
 * @version 1.0.0
 * @author Eng. Letzer Cartagena Negrón
 */
export function MetaTags() {
  const { locale } = useLocale();
  const seo = DEFAULT_SEO[locale];

  return (
    <>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <link rel="canonical" href={seo.canonicalUrl} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={seo.canonicalUrl} />
      <meta property="og:image" content={seo.ogImage} />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.ogImage} />
    </>
  );
}
